/**
 * Unomi segment condition utilities — parse/build/validate for the segment UI.
 */

const NUMERIC_PROPERTIES = new Set([
    "properties.nbOfVisits",
    "properties.pageViewCount",
    "properties.age",
    "properties.totalTimeSpent",
    "properties.kids",
    "properties.mealFrequency",
]);

const DATE_PROPERTIES = new Set([
    "properties.lastVisit",
    "properties.firstVisit",
    "properties.birthDate",
    "properties.lastActivityDate",
    "properties.purchaseHistory",
]);

const BOOLEAN_PROPERTIES = new Set([
    "properties.pushEnabled",
    "properties.isAnonymous",
    "properties.doNotContact",
]);

/**
 * Convert a date-picker value (YYYY-MM-DD) or ISO string into the format Unomi
 * expects for propertyValueDate, e.g. "2023-01-01T00:00:00Z".
 */
export function toUnomiDate(value) {
    if (value == null || value === "") return "";
    if (typeof value === "string") {
        // Plain calendar date from <input type="date"> — keep the exact day (no TZ shift).
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00:00Z`;
        if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
            const d = new Date(value);
            if (!Number.isNaN(d.getTime())) return d.toISOString().replace(/\.\d{3}Z$/, "Z");
            return value;
        }
    }
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString().replace(/\.\d{3}Z$/, "Z");
    return String(value);
}

/** Convert a stored value (ISO or YYYY-MM-DD) into a value for <input type="date">. */
export function toDateInputValue(value) {
    if (!value) return "";
    if (typeof value === "string") {
        const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) return m[1];
    }
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return "";
}

/** Resolve the input type (number/date/boolean/string) for a property id. */
export function fieldTypeForProperty(prop) {
    if (NUMERIC_PROPERTIES.has(prop)) return "number";
    if (DATE_PROPERTIES.has(prop)) return "date";
    if (BOOLEAN_PROPERTIES.has(prop)) return "boolean";
    return "string";
}

export const UNOMI_COMPARISON_OPERATORS = [
    { id: "equals", label: "égal à" },
    { id: "notEquals", label: "différent de" },
    { id: "greaterThan", label: "supérieur à" },
    { id: "greaterThanOrEqualTo", label: "supérieur ou égal à" },
    { id: "lessThan", label: "inférieur à" },
    { id: "lessThanOrEqualTo", label: "inférieur ou égal à" },
    { id: "startsWith", label: "commence par" },
    { id: "endsWith", label: "se termine par" },
    { id: "contains", label: "contient" },
    { id: "between", label: "entre" },
    { id: "exists", label: "existe" },
];

/** Fallback catalog when the API is unavailable (mirrors SegmentChain.ALLOWED_PROPERTIES). */
export const DEFAULT_UNOMI_PROPERTY_FIELDS = [
    "properties.nbOfVisits", "properties.pageViewCount", "properties.lastVisit",
    "properties.firstVisit", "properties.firstName", "properties.lastName",
    "properties.levelOfSatisfaction", "properties.email", "properties.jobTitle",
    "properties.nationality", "properties.phoneNumber", "properties.birthDate",
    "properties.maritalStatus", "properties.address", "properties.city",
    "properties.facebookId", "properties.countryName", "properties.kids",
    "properties.levelOfEducation", "properties.surename", "properties.formOfComunication",
    "properties.gender", "properties.age", "properties.profession", "properties.language",
    "properties.totalTimeSpent", "properties.country", "properties.postalCode",
    "properties.state", "properties.purchaseHistory", "properties.lastActivityDate",
    "properties.customerType", "properties.subscriptionStatus", "properties.preferredChannel",
    "properties.dietaryPreferences", "properties.foodAllergies", "properties.favoriteCuisine",
    "properties.cookingSkills", "properties.mealFrequency", "properties.pushId",
    "properties.pushEnabled", "properties.isAnonymous", "properties.doNotContact",
    "properties.whatsappWaId", "properties.instagramId",
].map((prop) => ({
    id: prop,
    label: prop.replace("properties.", ""),
    type: fieldTypeForProperty(prop),
}));

export const KNOWN_PROPERTY_IDS = new Set(
    DEFAULT_UNOMI_PROPERTY_FIELDS.map((f) => f.id)
);

/** Ensure property ids use the properties.* prefix expected by Unomi. */
export function normalizePropertyId(prop) {
    if (!prop) return "";
    if (prop === "lastActivityDate") return "properties.lastActivityDate";
    if (prop.startsWith("properties.")) return prop;
    return `properties.${prop}`;
}

export function isKnownProperty(prop) {
    const normalized = normalizePropertyId(prop);
    return KNOWN_PROPERTY_IDS.has(normalized);
}

/**
 * Normalize API response: supports { items: [{id,label,type}] } or { items: ["properties.x"] }.
 */
export function normalizeConditionFieldsResponse(data) {
    const raw = data?.items;
    if (!Array.isArray(raw) || raw.length === 0) return [];

    if (typeof raw[0] === "string") {
        return raw
            .filter((p) => p && p !== "properties.brand")
            .map((prop) => ({
                id: prop,
                label: prop.replace("properties.", "").replace("customer.", ""),
                type: fieldTypeForProperty(prop),
            }));
    }

    return raw
        .filter((f) => f?.id && f.id !== "properties.brand")
        .map((f) => ({
            id: f.id,
            label: f.label || f.id.replace("properties.", ""),
            type: f.type || "string",
        }));
}

const genId = () => `u_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export function isBrandCondition(node) {
    return (
        node?.type === "profilePropertyCondition" &&
        node?.parameterValues?.propertyName === "properties.brand"
    );
}

function leafValue(params) {
    if (!params) return "";
    // Collection values (e.g. "between") — keep as array [lower, upper].
    if (Array.isArray(params.propertyValuesDate)) return params.propertyValuesDate;
    if (Array.isArray(params.propertyValuesInteger)) return params.propertyValuesInteger;
    if (Array.isArray(params.propertyValues)) return params.propertyValues;
    if (params.propertyValueBoolean != null) return params.propertyValueBoolean;
    if (params.propertyValueInteger != null) return params.propertyValueInteger;
    if (params.propertyValueDate != null) return params.propertyValueDate;
    if (params.propertyValueDateExpr != null) return params.propertyValueDateExpr;
    return params.propertyValue ?? "";
}

function parseUnomiNode(node, { dropUnknown = true } = {}) {
    if (!node || typeof node !== "object") return null;

    if (node.type === "profilePropertyCondition") {
        if (isBrandCondition(node)) return null;
        const p = node.parameterValues || {};
        const property = normalizePropertyId(p.propertyName || "");
        if (dropUnknown && property && !KNOWN_PROPERTY_IDS.has(property)) {
            return null;
        }
        return {
            id: genId(),
            type: "condition",
            property,
            operator: p.comparisonOperator || "equals",
            value: leafValue(p),
        };
    }

    if (node.type === "booleanCondition") {
        const op = (node.parameterValues?.operator || "and").toLowerCase();
        const children = (node.parameterValues?.subConditions || [])
            .map((sub) => parseUnomiNode(sub, { dropUnknown }))
            .filter(Boolean);
        if (!children.length) return null;
        if (children.length === 1) return children[0];
        return {
            id: genId(),
            type: "group",
            conditions: children.map((c, i) => ({
                ...c,
                ...(i > 0 ? { joinWith: op } : {}),
            })),
        };
    }

    return null;
}

function packRowsWithJoins(rows) {
    if (!rows?.length) return null;

    const orChunks = [];
    let andChunk = [];

    rows.forEach((row, i) => {
        const node = serializeRow(row);
        if (!node) return;

        if (i === 0) {
            andChunk.push(node);
            return;
        }

        const join = row.joinWith || "and";
        if (join === "or") {
            if (andChunk.length) orChunks.push(andChunk);
            andChunk = [node];
        } else {
            andChunk.push(node);
        }
    });

    if (andChunk.length) orChunks.push(andChunk);

    const orSubs = orChunks.map((chunk) => {
        if (chunk.length === 1) return chunk[0];
        return {
            type: "booleanCondition",
            parameterValues: { operator: "and", subConditions: chunk },
        };
    });

    if (!orSubs.length) return null;
    if (orSubs.length === 1) return orSubs[0];
    return {
        type: "booleanCondition",
        parameterValues: { operator: "or", subConditions: orSubs },
    };
}

function expandSubForFlatten(sub, opts, joinWith, parentOp) {
    if (sub.type === "profilePropertyCondition") {
        const parsed = parseUnomiNode(sub, opts);
        return parsed ? [{ ...parsed, ...(joinWith ? { joinWith } : {}) }] : [];
    }

    if (sub.type !== "booleanCondition") return [];

    const subOp = (sub.parameterValues?.operator || "and").toLowerCase();
    const subSubs = (sub.parameterValues?.subConditions || []).filter((s) => !isBrandCondition(s));

    // OR(AND(A,B), C) → flat rows with ET inside each OR chunk.
    if (parentOp === "or" && subOp === "and") {
        const allLeaves = subSubs.length > 0 && subSubs.every((s) => s.type === "profilePropertyCondition");
        if (allLeaves) {
            return subSubs
                .map((s, i) => {
                    const parsed = parseUnomiNode(s, opts);
                    return parsed ? { ...parsed, joinWith: i === 0 ? joinWith : "and" } : null;
                })
                .filter(Boolean);
        }
    }

    // AND(A, OR(B,C)) cannot flatten without changing semantics — keep as nested group.
    if (parentOp === "and" && subOp === "or") {
        const parsed = parseUnomiNode(sub, opts);
        return parsed ? [{ ...parsed, ...(joinWith ? { joinWith } : {}) }] : [];
    }

    if (subOp === parentOp) {
        return flattenBooleanToRows(sub, opts, joinWith);
    }

    if (parentOp === "and" && subOp === "and") {
        return flattenBooleanToRows(sub, opts, joinWith);
    }

    if (parentOp === "or" && subOp === "or") {
        return flattenBooleanToRows(sub, opts, joinWith);
    }

    const parsed = parseUnomiNode(sub, opts);
    return parsed ? [{ ...parsed, ...(joinWith ? { joinWith } : {}) }] : [];
}

function flattenBooleanToRows(node, opts, joinWith) {
    if (!node) return [];

    if (node.type === "profilePropertyCondition") {
        const parsed = parseUnomiNode(node, opts);
        return parsed ? [{ ...parsed, ...(joinWith ? { joinWith } : {}) }] : [];
    }

    if (node.type !== "booleanCondition") return [];

    const op = (node.parameterValues?.operator || "and").toLowerCase();
    const subs = (node.parameterValues?.subConditions || []).filter((s) => !isBrandCondition(s));

    if (!subs.length) return [];
    if (subs.length === 1) return flattenBooleanToRows(subs[0], opts, joinWith);

    const rows = [];
    subs.forEach((sub, i) => {
        const itemJoin = i === 0 ? joinWith : op;
        rows.push(...expandSubForFlatten(sub, opts, itemJoin, op));
    });
    return rows;
}

/** Ensure the first row never carries joinWith; following rows default to ET. */
export function reconcileRowJoins(rows) {
    return (rows || []).map((row, i) => {
        if (i === 0) {
            const { joinWith, ...rest } = row;
            return rest;
        }
        return { ...row, joinWith: row.joinWith || "and" };
    });
}

/** Strip brand scope and convert Unomi JSON → builder state. */
export function parseUnomiConditionToRows(condition, { dropUnknown = true } = {}) {
    const parseOpts = { dropUnknown };
    const empty = { combinator: "and", rows: [] };
    if (!condition || typeof condition !== "object") return empty;

    let root = condition;
    if (
        root.type === "booleanCondition" &&
        (root.parameterValues?.operator || "and").toLowerCase() === "and"
    ) {
        const subs = root.parameterValues?.subConditions || [];
        const nonBrand = subs.filter((s) => !isBrandCondition(s));
        if (nonBrand.length === 1) {
            root = nonBrand[0];
        } else if (nonBrand.length > 1) {
            root = {
                type: "booleanCondition",
                parameterValues: { operator: "and", subConditions: nonBrand },
            };
        } else {
            return empty;
        }
    }

    const rows = flattenBooleanToRows(root, parseOpts);
    return { combinator: "and", rows: reconcileRowJoins(rows) };
}

function serializeRow(row) {
    if (row.type === "group") {
        return packRowsWithJoins(row.conditions || []);
    }

    if (row.type === "condition" && row.property) {
        const normProp = normalizePropertyId(row.property);
        const params = {
            propertyName: normProp,
            comparisonOperator: row.operator || "equals",
        };
        if (params.comparisonOperator === "between") {
            // Unomi "between" expects a 2-element collection [lower, upper].
            const arr = Array.isArray(row.value) ? row.value : [row.value, ""];
            if (NUMERIC_PROPERTIES.has(normProp)) {
                params.propertyValuesInteger = arr
                    .map((v) => Number(v))
                    .filter((n) => !Number.isNaN(n));
            } else if (DATE_PROPERTIES.has(normProp)) {
                params.propertyValuesDate = arr.map(toUnomiDate).filter(Boolean);
            } else {
                params.propertyValues = arr
                    .map((v) => (v != null ? String(v) : ""))
                    .filter((s) => s !== "");
            }
        } else if (params.comparisonOperator !== "exists") {
            const raw = row.value;
            if (BOOLEAN_PROPERTIES.has(normProp)) {
                params.propertyValueBoolean = raw === true || raw === "true";
            } else if (NUMERIC_PROPERTIES.has(normProp) && raw !== "" && raw != null) {
                const n = Number(raw);
                if (!Number.isNaN(n)) params.propertyValueInteger = n;
                else params.propertyValue = String(raw);
            } else if (DATE_PROPERTIES.has(normProp)) {
                if (raw !== "" && raw != null) {
                    params.propertyValueDate = toUnomiDate(raw);
                }
            } else {
                params.propertyValue = raw != null ? String(raw) : "";
            }
        }
        return { type: "profilePropertyCondition", parameterValues: params };
    }
    return null;
}

/** Convert builder state → Unomi condition (brand added by backend). */
export function buildUnomiConditionFromRows(_combinator, rows) {
    return packRowsWithJoins(rows || []);
}

export function validateUnomiCondition(condition) {
    if (!condition || typeof condition !== "object") {
        return { valid: false, error: "La condition doit être un objet JSON." };
    }
    if (!condition.type) {
        return { valid: false, error: "Champ 'type' manquant." };
    }
    if (condition.type === "profilePropertyCondition") {
        const p = condition.parameterValues;
        if (!p?.propertyName) return { valid: false, error: "propertyName requis." };
        if (!p?.comparisonOperator) return { valid: false, error: "comparisonOperator requis." };
        return { valid: true };
    }
    if (condition.type === "idsCondition") {
        // Manual membership node; managed via the membership API, not the builder.
        return { valid: true };
    }
    if (condition.type === "booleanCondition") {
        const subs = condition.parameterValues?.subConditions;
        if (!Array.isArray(subs) || subs.length === 0) {
            return { valid: false, error: "subConditions doit être un tableau non vide." };
        }
        for (const sub of subs) {
            const r = validateUnomiCondition(sub);
            if (!r.valid) return r;
        }
        return { valid: true };
    }
    return { valid: false, error: `Type de condition non supporté: ${condition.type}` };
}

export function formatSegmentDefinition(segment, brand) {
    const scope = segment?.scope || segment?.metadata?.scope || brand || "batira";
    return {
        itemId: segment?.itemId || segment?.id,
        itemType: segment?.itemType || "segment",
        version: segment?.version || 1,
        condition: segment?.condition || {
            type: "booleanCondition",
            parameterValues: {
                operator: "and",
                subConditions: [
                    {
                        type: "profilePropertyCondition",
                        parameterValues: {
                            propertyName: "properties.brand",
                            comparisonOperator: "equals",
                            propertyValue: scope,
                        },
                    },
                ],
            },
        },
        metadata: segment?.metadata || {
            id: segment?.id,
            name: segment?.name,
            description: segment?.description || "",
            scope,
            tags: segment?.tags || [],
            systemTags: segment?.systemTags || [],
            enabled: segment?.enabled !== undefined ? segment?.enabled : true,
            missingPlugins: segment?.missingPlugins !== undefined ? segment?.missingPlugins : false,
            hidden: segment?.hidden !== undefined ? segment?.hidden : false,
            readOnly: segment?.readOnly !== undefined ? segment?.readOnly : false,
        },
    };
}

const OP_LABELS = Object.fromEntries(
    UNOMI_COMPARISON_OPERATORS.map((o) => [o.id, o.label])
);

/** French labels for boolean operators so the summary reads consistently. */
const BOOLEAN_OP_LABELS = { and: "ET", or: "OU" };

function summarizeNode(node) {
    if (!node) return "";
    if (node.type === "profilePropertyCondition") {
        if (isBrandCondition(node)) return "";
        const p = node.parameterValues || {};
        const prop = normalizePropertyId(p.propertyName || "").replace("properties.", "");
        if (prop === "brand") return "";
        if (prop && !KNOWN_PROPERTY_IDS.has(normalizePropertyId(p.propertyName || ""))) return "";
        const op = OP_LABELS[p.comparisonOperator] || p.comparisonOperator;
        if (p.comparisonOperator === "exists") return `${prop} ${op}`;
        const val = leafValue(p);
        return `${prop} ${op} ${val}`;
    }
    if (node.type === "booleanCondition") {
        const rawOp = (node.parameterValues?.operator || "and").toLowerCase();
        const op = BOOLEAN_OP_LABELS[rawOp] || rawOp.toUpperCase();
        const parts = (node.parameterValues?.subConditions || [])
            .map(summarizeNode)
            .filter(Boolean);
        if (!parts.length) return "";
        if (parts.length === 1) return parts[0];
        return `(${parts.join(` ${op} `)})`;
    }
    return "";
}

export function summarizeCondition(condition, brand) {
    if (!condition) return `Marque = ${brand || "—"}`;
    const summary = summarizeNode(condition);
    const brandPart = `Marque = ${brand || "—"}`;
    return summary ? `${brandPart} ET ${summary}` : brandPart;
}

export function defaultEmptyRows() {
    return {
        combinator: "and",
        rows: [],
    };
}
